import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { concatMap, filter, of, withLatestFrom } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import * as PortalTeamsActions from './portal-teams.actions';
import { PortalTeamsService } from '../services/portal-teams.service';
import { Store } from '@ngrx/store';
import { PortalTeamsSelectors } from '@qwyk/hub/administration/data-access/portal-teams';

@Injectable()
export class PortalTeamsEffects {
    loadPortalTeams$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PortalTeamsActions.loadPortalTeams),
            switchMap(action =>
                this.service.getTeams(action.query).pipe(
                    map(response =>
                        PortalTeamsActions.loadPortalTeamsSuccess({
                            portalTeams: response.data,
                            pagination: response.meta,
                        })
                    ),
                    catchError(error =>
                        of(
                            PortalTeamsActions.loadPortalTeamsFailure({
                                error,
                            })
                        ))
                )
            )
        )
    );

    createPortalTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PortalTeamsActions.createPortalTeam),
            switchMap(action =>
                this.service.createPortalTeam(action.payload).pipe(
                    map(portalTeam =>
                        PortalTeamsActions.createPortalTeamSuccess({
                            portalTeam,
                        })
                    ),
                    catchError(error =>
                        of(
                            PortalTeamsActions.createPortalTeamFailure({
                                error,
                            })
                        )
                    )
                )
            )
        )
    );

    updatePortalTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PortalTeamsActions.updatePortalTeam),
            switchMap(action =>
                this.service.updatePortalTeam(action.id, action.payload).pipe(
                    map(portalTeam =>
                        PortalTeamsActions.updatePortalTeamSuccess({
                            portalTeam,
                        })
                    ),
                    catchError(error =>
                        of(
                            PortalTeamsActions.updatePortalTeamFailure({
                                error,
                            })
                        )
                    )
                )
            )
        )
    );

    updatePortalTeamSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    PortalTeamsActions.createPortalTeamSuccess,
                    PortalTeamsActions.updatePortalTeamSuccess
                ),
                tap(() => {
                    this.router.navigate(['/administration', 'portal-teams']);
                })
            ),
        { dispatch: false }
    );

    loadTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PortalTeamsActions.loadPortalTeam),
            concatMap(action => {
                return of(action).pipe(
                    withLatestFrom(this.store.select(PortalTeamsSelectors.getPortalTeamsEntities)),
                    filter(([{ teamId }, entities]) => !entities[teamId]),
                    switchMap(([{ teamId }]) => {
                        return this.service.getPortalTeam(teamId).pipe(
                            map(team => PortalTeamsActions.loadPortalTeamSuccess({ portalTeam: team })),
                            catchError(error =>
                                of(PortalTeamsActions.loadPortalTeamFailure({ error }))
                            )
                        )
                    })
                )
            }
            )
        )
    );

    constructor(
        private store: Store,
        private router: Router,
        private actions$: Actions,
        private service: PortalTeamsService,
    ) {}
}
